import { Component, OnInit } from '@angular/core';
import { BaseComponent } from '../../../../core/abstracts/baseComponent';
import { Actions, ofType } from '@ngrx/effects';
import { tap } from 'rxjs';
import { State } from '../../../../core/store';
import { Store, select } from '@ngrx/store';
import { AlertService } from 'src/app/shared/services/alert.service';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { markFormGroupTouched, updateFormGroupValidity } from '../../../../helpers/utils';
import { loadEmployeeUsers } from '../../../../core/store/employee/employee.action';
import { EmployeeReferralModel } from '../../../../models/employeeReferralModel';
import { closeEditEmployeeReferral, getEmployeeReferral, saveEmployeeReferral, saveEmployeeReferralSuccess } from '../../../../core/store/queues/queues.action';
import { ProspectiveCandidateModel } from '../../../../models/prospectiveCandidateModel';
import { formatDate } from '@angular/common';
import { UserAutoCompleteItem } from '../../../../models/utilModels';

@Component({
  selector: 'app-edit-referral',
  templateUrl: './edit-referral.component.html',
  styleUrls: ['./edit-referral.component.scss'],  
  standalone: false
})

export class EditReferralComponent extends BaseComponent implements OnInit {
  display: boolean = false;
  title: string = "New Employee Referral";
  form: UntypedFormGroup;
  employeeReferralId: number = 0;
  newHireSelected: boolean = false;

  prospectiveCandidate: ProspectiveCandidateModel;

  userId: number;
  employeeName: string;

  userList: UserAutoCompleteItem[];
  loadingReferral: boolean = false;
  editingReferral: boolean = false;
  disableReferredByName: boolean = false;
  disableNewHireName: boolean = false;
  showProspectiveCandidate: boolean = false;

  constructor(
    private actions$: Actions,
    private store: Store<State>,
    private alertService: AlertService,
    private fb: UntypedFormBuilder) {
    super();
  }

  ngOnInit(): void {
    this.form = this.createForm();
    this.subs.push(
      this.store.select(s => s.queuesState.editingEmployeeReferral).subscribe(editing => {
        if (this.display && !editing) {
          this.resetForm();
        }

        this.display = editing;
      }),
      this.store.select(s => s.queuesState.savingEmployeeReferral).subscribe(saving => {
        this.editingReferral = saving;
      }),
      this.store.select(s => s.queuesState.loadingEmployeeReferral).subscribe(saving => {
        this.loadingReferral = saving;
      }),
      this.store.select(s => s.queuesState.employeeReferralId).subscribe(id => {
        this.employeeReferralId = id;
        this.title = "New Employee Referral";

        if (typeof id !== 'undefined' && id > 0) {
          this.store.dispatch(getEmployeeReferral({ referralId: id }));
        }
      }),
      this.store.select(s => s.queuesState.selectedEmployeeReferral).subscribe(selectedReferral => {
        if (selectedReferral == null) {
          this.resetForm();
          return;
        }

        this.title = "Edit Employee Referral #" + selectedReferral.id;
        this.form.get("id").setValue(selectedReferral.id);
        this.form.get("referredByUserID").setValue(selectedReferral.referredByUserID);
        this.form.get("referredByName").setValue(selectedReferral.referredByName);
        if (selectedReferral.referredByUserID > 0) {
          this.form.get("selectedReferredBy").patchValue({
            name: selectedReferral.referredByName,
            id: selectedReferral.referredByUserID
          });
        }

        this.form.get("newHireUserID").setValue(selectedReferral.newHireUserID);
        this.form.get("newHireName").setValue(selectedReferral.newHireName);
        if (selectedReferral.newHireUserID > 0) {
          this.newHireSelected = true;
          this.form.get("selectedNewHire").patchValue({
            name: selectedReferral.newHireName,
            id: selectedReferral.newHireUserID
          });
        }

        if (selectedReferral.prospectiveCandidateID > 0) {
          this.form.get("prospectiveCandidateID").setValue(selectedReferral.prospectiveCandidateID);
          this.form.get("prospectiveCandidateName").setValue(selectedReferral.prospectiveCandidateName);
          this.showProspectiveCandidate = true;
        }
        else if (this.prospectiveCandidate != null) {
          this.form.get("prospectiveCandidateID").setValue(this.prospectiveCandidate.id);
          this.form.get("prospectiveCandidateName").setValue(this.prospectiveCandidate.firstName + " " + this.prospectiveCandidate.lastName);
          this.showProspectiveCandidate = true;
        }

        if (selectedReferral.nextBonusDate != null) {
          this.form.get("nextBonusDateString").setValue(formatDate(selectedReferral.nextBonusDate, 'yyyy-MM-dd', 'en-us'));
        }
      }),
      this.store.select(s => s.queuesState.selectedProspectiveCandidate).subscribe(selectedCandidate => {
        if (selectedCandidate == null) {
          this.prospectiveCandidate = null;
          return;
        }

        this.form.get("prospectiveCandidateID").setValue(selectedCandidate.id);
        this.form.get("prospectiveCandidateName").setValue(selectedCandidate.firstName + " " + selectedCandidate.lastName);

        this.showProspectiveCandidate = true;
      }),
      this.store.select(s => s.employeeState.employeeUsers).subscribe(users => {
        this.userList = users.map(x => ({
          id: x.value,
          name: x.label
        }));
      }),
      this.actions$.pipe(
        ofType(saveEmployeeReferralSuccess),
        tap(action => {
          this.alertService.success("Referral saved!");
          this.closeModal();
        })
      ).subscribe()
    );
  }

  disableButtons() {
    return this.editingReferral || this.loadingReferral;
  }

  resetForm() {
    this.showProspectiveCandidate = false;
    this.newHireSelected = false;
    this.form.reset({
      id: 0,
      referredByUserID: 0,
      referredByName: "",
      selectedReferredBy: {
        id: 0,
        name: ""
      },
      newHireUserID: 0,
      newHireName: "",
      selectedNewHire: {
        id: 0,
        name: ""
      },
      prospectiveCandidateID: 0,
      prospectiveCandidateName: "",
      nextBonusDateString: ""
    });
  }

  createForm() {
    let f = this.fb.group({
      id: this.fb.control(0),
      referredByUserID: this.fb.control(0),
      referredByName: this.fb.control(""),
      selectedReferredBy: this.fb.control({
        id: 0,
        name: ""
      }),
      newHireUserID: this.fb.control(0),
      newHireName: this.fb.control(""),
      selectedNewHire: this.fb.control({
        id: 0,
        name: ""
      }),
      prospectiveCandidateID: this.fb.control(0),
      prospectiveCandidateName: this.fb.control([{ value: "", disabled: true }]),
      nextBonusDateString: this.fb.control("")
    });

    return f;
  }

  getStaffList(event) {
    this.store.dispatch(loadEmployeeUsers({
      prefixText: event.query,
      campusId: 0,
      payrollClassificationId: 0,
      showInactive: false,
      showTerminated: false
    }));
  }

  selectReferredByStaff(selectedUser) {
    if (typeof selectedUser.value.id !== 'number') {
      return;
    }

    this.form.get("referredByUserID").setValue(selectedUser.value.id);
    this.form.get("referredByName").setValue(selectedUser.value.name);
  }

  clearReferredByStaff(event) {    
    this.form.get("referredByUserID").setValue(0);
    this.form.get("referredByName").setValue("");
  }

  selectNewHireStaff(selectedUser) {
    if (typeof selectedUser.value.id !== 'number') {
      return;
    }

    this.newHireSelected = true;
    this.form.get("newHireUserID").setValue(selectedUser.value.id);
    this.form.get("newHireName").setValue(selectedUser.value.name);
  }

  clearNewHireStaff(event) {    
    this.newHireSelected = false;
    this.form.get("newHireUserID").setValue(0);
    this.form.get("newHireName").setValue("");
  }

  closeModal() {
    this.store.dispatch(closeEditEmployeeReferral());
  }

  saveReferral() {
    updateFormGroupValidity(this.form);
    if (this.form.valid) {
      this.store.dispatch(saveEmployeeReferral({ referral: this.form.value as EmployeeReferralModel }));
    }
    else {
      markFormGroupTouched(this.form);
    }
  }

}
